// © Microsoft Corporation. All rights reserved.

import { get, set } from "lodash-es";

import { ConnectorConstants } from "../constants/ConnectorConstants";
import { ConnectorNamespaces } from "@msnews/experiences-constants";

/**
 * Maps the data connector name to the set of experience keys it is injected into.
 * The values include the "._@STATE@_" portion needed to pull experience state out of the app state object.
 */
const dataConnectorToExperienceKeyMap: Map<string, Set<string>> = new Map<string, Set<string>>();

/**
 * Register a data connector to inject into an experience.
 * @param experienceFullNamespace The full namespace of the experience to inject into.
 * @param dataConnectorName The name for the data connector to inject.
 */
function registerInjection(
    experienceFullNamespace: string,
    dataConnectorName: ConnectorNamespaces): void {
    let experienceSet: Set<string> = dataConnectorToExperienceKeyMap.get(dataConnectorName);
    if (!experienceSet) {
        experienceSet = new Set<string>();
        dataConnectorToExperienceKeyMap.set(dataConnectorName, experienceSet);
    }

    experienceSet.add(createStatePropertyKey(experienceFullNamespace));
}

/**
 * Initializes the shared data connector state for the given experience.
 * @param appState The global app state to modify.
 * @param experienceFullNamespace The full namespace of the experience to host the data connector state.
 * @param dataConnectorName The name of the data connector to use.
 */
function initializeExperience(
    appState: any,
    experienceFullNamespace: string,
    dataConnectorName: ConnectorNamespaces): void {

    // Setup the key and get the actual state property for the connector. lodash uses dotted notation
    // so the key will be something like: namespace._@STATE@_
    const experienceStatePropertyKey = createStatePropertyKey(experienceFullNamespace);
    const connectorPreviousState = get(appState, experienceStatePropertyKey, undefined);

    // Find the initial data connector state
    const initialDataConnectorState = get(appState, createStatePropertyKey(dataConnectorName), undefined);

    // Create the new experience connector state
    const nextStateForConnector = { ...connectorPreviousState, [dataConnectorName]: initialDataConnectorState };
    set(appState, experienceStatePropertyKey, nextStateForConnector);
}

/**
 * Updates the data connector state on all experiences that include it.
 * @param appState The global app state object.
 * @param dataConnectorNamespace The namespace of the data connector that's been updated.
 * @param newDataConnectorState The new data connector state object.
 */
function reportStateChange(
    appState: any,
    dataConnectorNamespace: string,
    newDataConnectorState: any): void {

    const experienceKeys: Set<string> = dataConnectorToExperienceKeyMap.get(dataConnectorNamespace);
    if (experienceKeys) {
        for (const experienceKey of experienceKeys) {
            const experiencePreviousState: any = get(appState, experienceKey, undefined);

            if (experiencePreviousState) {
                set(appState, experienceKey, { ...experiencePreviousState, [dataConnectorNamespace]: newDataConnectorState });
            }
        }
    }
}

/**
 * Creates the lodash property key to get or set the experience or data connector state.
 * @param fullNamespace The full namespace of the experience or data connector.
 */
function createStatePropertyKey(fullNamespace: string): string {
    return fullNamespace + "." + ConnectorConstants.connectorStateKey;
}

/** Methods to support injecting data connector state into experiences */
export const dataConnectorInjector = {
    registerInjection,
    initializeExperience,
    reportStateChange
};
